import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../../firebase-client';
import { collection, getDocs } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../../../utils/firestoreSubscriptions';
import { resolveRsvpSummary, toRsvpRowFromSnapshot } from '../../../../../utils/rsvpResolver';
import { reportCaughtError } from '../../../../../utils/clientErrorReporting';
import { isRecurringRsvpEvent } from '../readOnlyEventPriority';
const setEventRsvpFn = httpsCallable(functions, 'setEventRsvp');
const resetEventRsvpFn = httpsCallable(functions, 'resetEventRsvp');
const getEventWaitlistFn = httpsCallable(functions, 'getEventWaitlist');
function toOccurrenceIdFromStart(startLike) {
  if (!startLike) return null;
  const d = new Date(startLike);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isPermissionDenied(error) {
  const code = String(error?.code || '').trim().toLowerCase();
  return code === 'permission-denied' || code === 'firestore/permission-denied';
}

export function useRsvp({
  form,
  teamId,
  userId: targetUserId,
  actorUserId = null,
  occurrenceIdOverride = null,
  rsvpSeriesResponsesEnabled = true
}) {
  const [myRsvpStatus, setMyRsvpStatus] = useState(null);
  const [myRsvpScope, setMyRsvpScope] = useState(null);
  const [myOccurrenceRsvpStatus, setMyOccurrenceRsvpStatus] = useState(null);
  const [mySeriesRsvpStatus, setMySeriesRsvpStatus] = useState(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [visibleYesCount, setVisibleYesCount] = useState(0);
  const [serverCapacityState, setServerCapacityState] = useState(null);
  const isRecurring = isRecurringRsvpEvent(form);
  const occurrenceId = occurrenceIdOverride || form.occurrenceId || toOccurrenceIdFromStart(form.start?.toDate ? form.start.toDate() : form.start) || (form.date ? toOccurrenceIdFromStart(`${form.date}T${form.time || '00:00'}`) : null) || null;
  const rawCapacity = form.capacity;
  const parsedCapacity = rawCapacity === '' || rawCapacity == null ? null : Number(rawCapacity);
  const capacityNum = Number.isFinite(parsedCapacity) ? parsedCapacity : null;
  const currentYesCount = serverCapacityState?.confirmedCount ?? visibleYesCount;
  const capacityRemaining = capacityNum == null
    ? null
    : (serverCapacityState?.capacityRemaining ?? Math.max(0, capacityNum - visibleYesCount));
  const isFull = capacityNum != null ? capacityRemaining <= 0 : false;
  const applyResolvedRsvpState = useCallback(rows => {
    const resolved = resolveRsvpSummary({
      rows,
      userId: targetUserId,
      occurrenceId: isRecurring ? occurrenceId : null,
      isRecurring,
      seriesResponsesEnabled: rsvpSeriesResponsesEnabled
    });
    setMyRsvpStatus(resolved.status);
    setMyRsvpScope(resolved.scope);
    setMyOccurrenceRsvpStatus(resolved.occurrenceStatus);
    setMySeriesRsvpStatus(resolved.seriesStatus);
    setVisibleYesCount(resolved.yesCount);
  }, [isRecurring, occurrenceId, rsvpSeriesResponsesEnabled, targetUserId]);
  const refreshCapacityState = useCallback(async () => {
    if (form.attendanceMode !== 'rsvp' || !form.id || !teamId || !targetUserId) {
      setServerCapacityState(null);
      return;
    }
    try {
      const result = await getEventWaitlistFn({
        teamId,
        eventId: form.id,
        ...(isRecurring && occurrenceId ? { occurrenceId } : {}),
        ...(actorUserId && actorUserId !== targetUserId ? { targetUserId } : {}),
      });
      const confirmedCount = Number(result.data?.confirmedCount);
      const reservedCount = Number(result.data?.reservedCount);
      const remaining = result.data?.capacityRemaining;
      setServerCapacityState({
        confirmedCount: Number.isFinite(confirmedCount) ? confirmedCount : 0,
        reservedCount: Number.isFinite(reservedCount) ? reservedCount : 0,
        capacityRemaining: remaining == null ? null : Math.max(0, Number(remaining) || 0),
      });
    } catch (err) {
      reportCaughtError(err, {
        source: 'event.rsvp.capacity',
        severity: 'error',
        context: { teamId, eventId: form.id },
      });
    }
  }, [actorUserId, form.attendanceMode, form.id, isRecurring, occurrenceId, targetUserId, teamId]);
  const refreshRsvpState = useCallback(async () => {
    if (form.attendanceMode !== 'rsvp' || !form.id || !teamId) {
      setRsvpLoading(false);
      return;
    }
    setRsvpLoading(true);
    try {
      const all = await getDocs(collection(db, 'teams', teamId, 'events', form.id, 'rsvps'));
      applyResolvedRsvpState(all.docs.map(toRsvpRowFromSnapshot).filter(Boolean));
    } catch (err) {
      if (!isPermissionDenied(err)) {
        reportCaughtError(err, {
          source: 'event.rsvp.read',
          severity: 'error',
          context: { phase: 'refresh', teamId, eventId: form.id },
        });
      }
    } finally {
      setRsvpLoading(false);
    }
  }, [form.attendanceMode, form.id, teamId, applyResolvedRsvpState]);
  useEffect(() => {
    if (form.attendanceMode !== 'rsvp' || !form.id || !teamId) {
      setMyRsvpStatus(null);
      setMyRsvpScope(null);
      setMyOccurrenceRsvpStatus(null);
      setMySeriesRsvpStatus(null);
      setVisibleYesCount(0);
      setServerCapacityState(null);
      setRsvpLoading(false);
      return undefined;
    }
    setRsvpLoading(true);
    const unsub = safeOnSnapshot(collection(db, 'teams', teamId, 'events', form.id, 'rsvps'), snap => {
      applyResolvedRsvpState(snap.docs.map(toRsvpRowFromSnapshot).filter(Boolean));
      setRsvpLoading(false);
    }, err => {
      // A cached event can remain visible briefly after team/card access is
      // removed. Firestore correctly rejects its RSVP listener in that case.
      if (!isPermissionDenied(err)) {
        reportCaughtError(err, {
          source: 'event.rsvp.read',
          severity: 'error',
          context: { phase: 'subscription', teamId, eventId: form.id },
        });
      }
      setMyRsvpStatus(null);
      setMyRsvpScope(null);
      setMyOccurrenceRsvpStatus(null);
      setMySeriesRsvpStatus(null);
      setVisibleYesCount(0);
      setRsvpLoading(false);
    });
    return () => unsub();
  }, [applyResolvedRsvpState, form.attendanceMode, form.id, teamId]);
  useEffect(() => {
    refreshCapacityState();
  }, [refreshCapacityState]);
  const applyLocalRsvpStatus = useCallback((status, scope, targetOccurrenceId) => {
    const normalizedScope = scope === 'occurrence' && targetOccurrenceId ? 'occurrence' : 'series';
    const previousEffectiveStatus = myRsvpStatus;
    const nextEffectiveStatus =
      normalizedScope === 'occurrence'
        ? status
        : (myOccurrenceRsvpStatus || status);

    if (normalizedScope === 'occurrence') {
      setMyOccurrenceRsvpStatus(status);
      setMyRsvpStatus(status);
      setMyRsvpScope('occurrence');
    } else {
      setMySeriesRsvpStatus(status);
      if (!myOccurrenceRsvpStatus) {
        setMyRsvpStatus(status);
        setMyRsvpScope('series');
      }
    }

    if (previousEffectiveStatus !== nextEffectiveStatus) {
      setVisibleYesCount((count) => {
        if (previousEffectiveStatus === 'yes' && nextEffectiveStatus !== 'yes') return Math.max(0, count - 1);
        if (previousEffectiveStatus !== 'yes' && nextEffectiveStatus === 'yes') return count + 1;
        return count;
      });
    }
  }, [myOccurrenceRsvpStatus, myRsvpStatus]);
  const setRsvpStatus = async (status, options = {}) => {
    const actorId = actorUserId || targetUserId;
    const writingForOtherMember = !!actorId && !!targetUserId && actorId !== targetUserId;
    const canMemberRsvp = form.attendanceMode === 'rsvp' && !!targetUserId && !!form.id && !!teamId;
    if (!canMemberRsvp) {
      // Optional: show a toast/alert while debugging
      // alert('RSVP not available for this event (missing user or event id).');
      return false;
    }
    if (status === 'yes' && isFull && myRsvpStatus !== 'yes') {
      alert('This event is full.');
      return false;
    }
    try {
      setRsvpBusy(true);
      const scope = options.scope || (isRecurring && occurrenceId ? 'occurrence' : 'series');
      if (isRecurring && rsvpSeriesResponsesEnabled === false && scope === 'series') {
        alert('This club requires a separate RSVP for each date.');
        return false;
      }
      const targetOccurrenceId = scope === 'occurrence' ? options.occurrenceId || occurrenceId : null;
      if (scope === 'occurrence' && !targetOccurrenceId) {
        alert('Choose a date first for this RSVP.');
        return false;
      }
      const contextualOccurrenceId = isRecurring ? (targetOccurrenceId || occurrenceId) : null;
      await setEventRsvpFn({
        teamId,
        eventId: form.id,
        targetUserId,
        status,
        scope,
        ...(contextualOccurrenceId ? {
          occurrenceId: contextualOccurrenceId
        } : {})
      });
      await Promise.all([refreshRsvpState(), refreshCapacityState()]);
      applyLocalRsvpStatus(status, scope, targetOccurrenceId);
      return true;
    } catch (err) {
      const detail = typeof err?.message === 'string' ? err.message.replace(/^FirebaseError:\s*/i, '').trim() : '';
      const fallback = 'Sorry, something went wrong saving your RSVP.';
      alert(detail && (writingForOtherMember || /full|waitlist|capacity/i.test(detail)) ? detail : fallback);
      return false;
    } finally {
      setRsvpBusy(false);
    }
  };
  const resetRsvpStatus = async (options = {}) => {
    const actorId = actorUserId || targetUserId;
    const writingForOtherMember = !!actorId && !!targetUserId && actorId !== targetUserId;
    const canMemberRsvp = form.attendanceMode === 'rsvp' && !!targetUserId && !!form.id && !!teamId;
    if (!canMemberRsvp) return false;
    try {
      setRsvpBusy(true);
      const targetOccurrenceId = isRecurring ? options.occurrenceId || occurrenceId || null : null;
      await resetEventRsvpFn({
        teamId,
        eventId: form.id,
        targetUserId,
        ...(options.mode ? {
          mode: options.mode
        } : {}),
        ...(targetOccurrenceId ? {
          occurrenceId: targetOccurrenceId
        } : {})
      });
      await Promise.all([refreshRsvpState(), refreshCapacityState()]);
      return true;
    } catch (err) {
      const detail = typeof err?.message === 'string' ? err.message.replace(/^FirebaseError:\s*/i, '').trim() : '';
      const fallback = 'Sorry, something went wrong resetting your RSVP.';
      alert(writingForOtherMember && detail ? detail : detail || fallback);
      return false;
    } finally {
      setRsvpBusy(false);
    }
  };
  return {
    myRsvpStatus,
    myRsvpScope,
    myOccurrenceRsvpStatus,
    mySeriesRsvpStatus,
    rsvpLoading,
    rsvpBusy,
    currentYesCount,
    capacityRemaining,
    capacityNum,
    isFull,
    isRecurring,
    occurrenceId,
    setRsvpStatus,
    resetRsvpStatus
  };
}
